/**
 * Implied FX rates from the portfolio total's currency breakdown.
 *
 * No endpoint publishes FX rates directly; the portfolio total's
 * per-currency breakdown carries each currency's value both locally and in
 * base currency, so `value_base_currency / value` is the rate the payload
 * itself is using. Anything that adds money ACROSS currencies (allocation
 * %, KPI P/L sums) must go through this map — local-currency figures are
 * not additive with base-currency ones.
 */

import type { CurrencyBreakdown } from '../services/portfolioApi';

/**
 * Build a `{ currency -> rate to base }` map. Currencies with a non-positive
 * local value can't imply a rate and are simply absent (callers fall back).
 */
export function buildCurrencyRateMap(
  breakdown: CurrencyBreakdown[] | undefined | null
): Record<string, number> {
  if (!breakdown) return {};

  const rates: Record<string, number> = {};
  breakdown.forEach((entry) => {
    if (entry.value > 0) {
      rates[entry.currency] = entry.value_base_currency / entry.value;
    }
  });
  return rates;
}

/**
 * Rate for one currency, defaulting to 1 (same-currency / unknown-currency
 * passthrough), matching how PositionTable has always converted.
 */
export function rateForCurrency(rates: Record<string, number>, currency: string): number {
  return rates[currency] || 1;
}
